import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEllipsisH, FaPencilAlt, FaTimes, FaPlus, FaUser, FaHistory, FaBolt, FaDownload, FaLaptop, FaDesktop, FaTv, FaTag, FaBarcode, FaMapMarkerAlt, FaUserMinus, FaUserPlus, FaPrint, FaCheckCircle, FaCopy, FaClipboardList } from 'react-icons/fa';
import { Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import TableView from './TableView';
import EmployeesWithoutEquipmentModal from './EmployeesWithoutEquipmentModal';
import UnassignAssetModal from './UnassignAssetModal';
import DeleteAssetModal from './DeleteAssetModal';
import NewAssetModal from './NewAssetModal';
import AssetModal from './AssetModal';
import EmployeeModal from './EmployeeModal';
import TimelineView from './TimelineView';
import AssignmentTimeline from './AssignmentTimeline';
import EquipmentLifeIndicator from './EquipmentLifeIndicator';
import InventoryAISummary from './InventoryAISummary';
import BrandLogo from './BrandLogo';
import Modal from './common/Modal';
import { QuickActionsMenu } from './common';
import './AssetsView.css';
import axiosInstance from '../utils/axiosConfig';
import { getApiError, getBlobApiError } from '../utils/apiError';

function AssetsView() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEmployeesModal, setShowEmployeesModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNewAssetModal, setShowNewAssetModal] = useState(false);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showIndividualTimeline, setShowIndividualTimeline] = useState(false);
  const [selectedAssetForTimeline, setSelectedAssetForTimeline] = useState(null);
  const [showAssignmentTimeline, setShowAssignmentTimeline] = useState(false);
  const [selectedAssetForAssignments, setSelectedAssetForAssignments] = useState(null);
  const [showAISummary, setShowAISummary] = useState(false);
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({
    sede: true,
    activo_fijo: true,
    tipo: true,
    marca: true,
    modelo: true,
    serial: true,
    procesador: false,
    ram: false,
    disco: false,
    estado: true,
    empleado: false,
    nombre_equipo: true,
    id: false,
    fecha_asignacion: false,
    vida_util: false,
    acciones: true,
  });
  const [sedes, setSedes] = useState([]);
  const [tipos, setTipos] = useState(['LAPTOP', 'DESKTOP', 'AIO']);
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [rams, setRams] = useState([]);
  const [discos, setDiscos] = useState([]);
  const [showConstanciaModal, setShowConstanciaModal] = useState(false);
  const [showADVerifyModal, setShowADVerifyModal] = useState(false);
  const [adEquipmentInfo, setADEquipmentInfo] = useState(null);
  const [filteredCount, setFilteredCount] = useState(0);
  const [filteredRows, setFilteredRows] = useState([]);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const getTypeIcon = (tipo) => {
    switch (tipo?.toUpperCase()) {
      case 'LAPTOP':
        return <FaLaptop className="type-icon laptop" title="Laptop" />;
      case 'DESKTOP':
        return <FaDesktop className="type-icon desktop" title="Desktop" />;
      case 'AIO':
        return <FaTv className="type-icon aio" title="All-in-One" />;
      default:
        return null;
    }
  };

  const columns = useMemo(
    () => [
      {
        header: 'Sede',
        accessorKey: 'sede',
        cell: ({ row, column, table }) => {
          const sede = row.original.sede;
          if (!sede) return '—';

          // Generar color único basado en hash del texto
          const stringToColor = (str) => {
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
              hash = str.charCodeAt(i) + ((hash << 5) - hash);
            }

            const hue = Math.abs(hash % 360);
            const saturation = 65 + (Math.abs(hash >> 8) % 20);
            const lightness = 85 + (Math.abs(hash >> 16) % 10);

            return {
              bg: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
              color: `hsl(${hue}, ${saturation + 20}%, 30%)`,
              border: `hsl(${hue}, ${saturation}%, ${lightness - 15}%)`
            };
          };

          const colorStyle = stringToColor(sede);

          return (
            <div
              className="badge-cell"
              style={{
                backgroundColor: colorStyle.bg,
                color: colorStyle.color,
                border: `1px solid ${colorStyle.border}`
              }}
            >
              <FaMapMarkerAlt className="badge-icon" style={{ color: colorStyle.color }} />
              <EditableCell
                value={sede}
                column={column}
                row={row}
                table={table}
                options={sedes.map(sede => ({ id: sede.id, nombre: sede.nombre }))}
                onSave={handleSave}
              />
            </div>
          );
        }
      },
      {
        header: 'Activo Fijo',
        accessorKey: 'activo_fijo',
        cell: ({ row, column, table }) => (
          <div className="badge-cell activo-fijo-badge">
            <FaTag className="badge-icon" />
            <EditableCell
              value={row.original.activo_fijo}
              column={column}
              row={row}
              table={table}
              options={[]}
              onSave={handleSave}
              isBadgeType={true}
            />
          </div>
        )
      },
      {
        header: 'Tipo',
        accessorKey: 'tipo',
        cell: ({ row, column, table }) => (
          <div className="type-cell">
            {getTypeIcon(row.original.tipo)}
            <EditableCell
              value={row.original.tipo}
              column={column}
              row={row}
              table={table}
              options={tipos}
              onSave={handleSave}
            />
          </div>
        )
      },
      {
        header: 'Marca',
        accessorKey: 'marca',
        cell: ({ row, column, table }) => (
          <div className="brand-cell">
            <BrandLogo brand={row.original.marca} />
          </div>
        )
      },
      {
        header: 'Modelo',
        accessorKey: 'modelo',
        cell: ({ row, column, table }) => (
          <EditableCell
            value={row.original.modelo}
            column={column}
            row={row}
            table={table}
            options={modelos}
            onSave={handleSave}
          />
        )
      },
      {
        header: 'Serial',
        accessorKey: 'serial',
        cell: ({ row, column, table }) => (
          <div className="badge-cell serial-badge">
            <FaBarcode className="badge-icon" />
            <EditableCell
              value={row.original.serial}
              column={column}
              row={row}
              table={table}
              options={[]}
              onSave={handleSave}
              isBadgeType={true}
            />
          </div>
        )
      },
      {
        header: 'Estado',
        accessorKey: 'estado',
        cell: ({ row }) => {
          const estado = row.original.estado?.toLowerCase() || '';
          const empleado = row.original.empleado;
          const manual = row.original.asignacion_manual;
          const empleadoId = row.original.empleado_id;

          if (estado === 'asignado' && empleado) {
            return (
              <span
                className="employee-type"
                onClick={() => handleViewEmployee(empleadoId)}
                title={empleado}
              >
                <FaUser className="employee-icon" />
                <span className="employee-name">{empleado}</span>
              </span>
            );
          }

          if (estado === 'asignado' && !empleado && manual) {
            return (
              <span className="manual-badge" title={manual}>
                <FaUser className="employee-icon" />
                {manual}
              </span>
            );
          }

          return (
            <span className={`estado-badge ${estado.replace(/\s+/g, '-')}`}>
              {row.original.estado}
            </span>
          );
        }
      },
      {
        header: 'Empleado',
        accessorKey: 'empleado',
        cell: ({ row }) => {
          const empleado = row.original.empleado || row.original.asignacion_manual;
          const empleadoId = row.original.empleado_id;

          if (!empleado) return <span className="no-employee">Sin asignar</span>;

          return (
            <span
              className="employee-type"
              onClick={() => handleViewEmployee(empleadoId)}
              title={empleado}
            >
              {row.original.empleado ? <FaUser className="employee-icon" /> : null}
              <span className="employee-name">{empleado}</span>
            </span>
          );
        }
      },
      {
        header: 'Nombre',
        accessorKey: 'nombre_equipo',
        cell: ({ row, getValue }) => (
          <EditableCell
            value={getValue()}
            column={{ id: 'nombre_equipo' }}
            row={row}
            table={row.table}
            options={[]}
            onSave={handleSave}
          />
        )
      },
      {
        header: 'ID',
        accessorKey: 'id',
      },
      {
        header: 'Procesador',
        accessorKey: 'procesador',
        cell: ({ row, column, table }) => (
          <EditableCell
            value={row.original.procesador}
            column={column}
            row={row}
            table={table}
            options={[]}
            onSave={handleSave}
          />
        )
      },
      {
        header: 'RAM',
        accessorKey: 'ram',
        cell: ({ row, column, table }) => (
          <EditableCell
            value={row.original.ram}
            column={column}
            row={row}
            table={table}
            options={rams}
            onSave={handleSave}
          />
        )
      },
      {
        header: 'Disco',
        accessorKey: 'disco',
        cell: ({ row, column, table }) => (
          <EditableCell
            value={row.original.disco}
            column={column}
            row={row}
            table={table}
            options={discos}
            onSave={handleSave}
          />
        )
      },
      {
        header: 'Fecha de Asignación',
        accessorKey: 'fecha_asignacion',
        cell: ({ row }) => {
          const fecha = row.original.fecha_asignacion;
          if (!fecha) return <span className="no-employee">Sin asignar</span>;
          return new Date(fecha).toLocaleDateString('es-ES');
        }
      },
      {
        header: 'Vida útil',
        id: 'vida_util',
        cell: ({ row }) => (
          <EquipmentLifeIndicator createdAt={row.original.created_at} compact />
        ),
      },
      {
        header: 'Acciones',
        id: 'acciones',
        cell: ({ row }) => (
          <div className={`action-buttons ${activeActionMenu === row.original.id ? 'menu-active' : ''}`}>
            <button
              onClick={() => handleView(row.original)}
              className="action-button view-button"
              title="Ver detalles"
            >
              <FaEllipsisH />
            </button>

            <button
              className="action-button history-button"
              onClick={() => {
                setSelectedAssetForAssignments(row.original);
                setShowAssignmentTimeline(true);
              }}
              title="Historial de asignaciones"
            >
              <FaHistory />
            </button>

            <div className="quick-actions-container" style={{ display: 'flex', alignItems: 'center' }}>
              <button
                className={`icon-button ${activeActionMenu === row.original.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (activeActionMenu === row.original.id) {
                    setActiveActionMenu(null);
                    setMenuAnchorRect(null);
                  } else {
                    setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
                    setActiveActionMenu(row.original.id);
                  }
                }}
                title="Acciones rápidas"
              >
                <FaBolt />
              </button>
              <AnimatePresence>
                {activeActionMenu === row.original.id && (
                  <QuickActionsMenu anchorRect={menuAnchorRect}>
                    {[
                      // Solo se ofrece asignar si el equipo está disponible: en
                      // reparación o desincorporado el backend lo rechaza
                      ...(() => {
                        const estado = (row.original.estado || '').toLowerCase();
                        const accion = {
                          onClick: () => {
                            handleAssignClick(row.original);
                            setActiveActionMenu(null);
                          }
                        };
                        if (estado === 'asignado') {
                          return [{ ...accion, icon: FaUserMinus, label: 'Desasignar equipo', color: '#EF4444' }];
                        }
                        if (!estado || estado === 'disponible') {
                          return [{ ...accion, icon: FaUserPlus, label: 'Asignar equipo', color: '#10B981' }];
                        }
                        return [];
                      })(),
                      {
                        icon: FaPrint,
                        label: 'Imprimir constancia',
                        color: '#3B82F6',
                        onClick: () => {
                          if (row.original.estado?.toLowerCase() === 'asignado') {
                            setSelectedAsset(row.original);
                            setShowConstanciaModal(true);
                          }
                          setActiveActionMenu(null);
                        }
                      },
                      {
                        icon: FaCheckCircle,
                        label: 'Verificar en AD',
                        color: '#8B5CF6',
                        onClick: () => {
                          handleVerifyAD(row.original);
                          setActiveActionMenu(null);
                        }
                      },
                      {
                        icon: FaCopy,
                        label: 'Copiar información',
                        color: '#F59E0B',
                        onClick: () => {
                          handleCopyInfo(row.original);
                          setActiveActionMenu(null);
                        }
                      },
                      ...(row.original.tipo?.toUpperCase() === 'LAPTOP' && row.original.estado?.toLowerCase() === 'asignado' ? [{
                        icon: FaClipboardList,
                        label: 'Hoja de vigilancia',
                        color: '#EC4899',
                        onClick: () => {
                          handleVigilanciaSheet();
                          setActiveActionMenu(null);
                        }
                      }] : [])
                    ].map((action) => (
                      <button
                        key={action.label}
                        className="quick-action-item"
                        onClick={action.onClick}
                      >
                        <span className="action-icon" style={{ color: action.color }}>
                          <action.icon />
                        </span>
                        <span className="action-label">{action.label}</span>
                      </button>
                    ))}
                  </QuickActionsMenu>
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={() => handleDelete(row.original)}
              className="action-button delete-button"
              title="Eliminar equipo"
            >
              <FaTimes />
            </button>
          </div>
        ),
      }
    ],
    [sedes, tipos, marcas, modelos, rams, discos, activeActionMenu]
  );

  useEffect(() => {
    // Defer data fetching to allow page transition animations to finish smoothly
    const timer = setTimeout(() => {
      fetchData();
      fetchSedes();
      fetchMarcas();
      fetchModelos();
      fetchRams();
      fetchDiscos();
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/activos');
      setData(response.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(`Error cargando datos: ${error.message}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSedes = async () => {
    try {
      const response = await axiosInstance.get('/api/sedes');
      setSedes(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchMarcas = async () => {
    try {
      const response = await axiosInstance.get('/api/marcas');
      setMarcas(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchModelos = async () => {
    try {
      const response = await axiosInstance.get('/api/modelos');
      setModelos(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchRams = async () => {
    try {
      const response = await axiosInstance.get('/api/rams');
      setRams(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchDiscos = async () => {
    try {
      const response = await axiosInstance.get('/api/discos');
      setDiscos(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleView = (asset) => {
    setSelectedAsset(asset);
    setShowAssetModal(true);
  };

  const handleAssignClick = (asset) => {
    setSelectedAsset(asset);
    if (asset.estado?.toLowerCase() === 'asignado') {
      setShowUnassignModal(true);
    } else {
      setShowModal(true);
    }
  };

  const handleDelete = (asset) => {
    setSelectedAsset(asset);
    setShowDeleteModal(true);
  };

  const handleAssignSuccess = () => {
    fetchData();
    setShowModal(false);
    setSelectedAsset(null);
  };

  const handleUnassignSuccess = () => {
    fetchData();
    setShowUnassignModal(false);
    setSelectedAsset(null);
  };

  const handleDeleteSuccess = () => {
    fetchData();
    setShowDeleteModal(false);
    setSelectedAsset(null);
  };

  const handleAssetAdded = (newAsset) => {
    setData(prevData => [...prevData, newAsset]);
    setShowNewAssetModal(false);
  };

  const handleViewEmployee = async (empleadoId) => {
    try {
      const response = await axiosInstance.get(`/api/empleados/${empleadoId}`);
      setSelectedEmployee(response.data);
      setShowEmployeeModal(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSave = async (id, field, value) => {
    try {
      console.log('handleSave called:', { id, field, value });
      const response = await axiosInstance.patch(`/api/activos/${id}`, { [field]: value });
      console.log('API Response:', response.data);

      if (response.data.message) {
        console.log('Updating state with:', { field, value, updates: response.data.updated_fields });
        setData(old =>
          old.map(row =>
            row.id === id
              ? { ...row, [field]: value, ...response.data.updated_fields }
              : row
          )
        );
        return true;
      }
      console.log('Update not successful');
      return false;
    } catch (error) {
      console.error('Error in handleSave:', error);
      toast.error(getApiError(error, 'No se pudo guardar el cambio'));
      return false;
    }
  };

  const handleVerifyAD = async (asset) => {
    try {
      console.log('Verificando equipo:', asset);

      if (!asset.nombre_equipo) {
        console.log('Equipo sin nombre');
        setADEquipmentInfo({
          expectedName: '',
          exists: false,
          error: 'El equipo no tiene nombre asignado',
          loading: false
        });
        setShowADVerifyModal(true);
        return;
      }

      setADEquipmentInfo({ expectedName: asset.nombre_equipo, loading: true });
      setShowADVerifyModal(true);

      console.log('Haciendo petición al backend para:', asset.nombre_equipo);
      const response = await axiosInstance.get('/api/verificar_equipos');
      console.log('Respuesta del backend:', response.data);

      const equipoInfo = response.data[asset.nombre_equipo];

      setADEquipmentInfo({
        expectedName: asset.nombre_equipo,
        ...equipoInfo,
        loading: false
      });
    } catch (error) {
      console.error('Error verificando equipo en AD:', error);
      console.error('Detalles del error:', {
        response: error.response,
        message: error.message,
        config: error.config
      });

      setADEquipmentInfo({
        expectedName: asset.nombre_equipo || '',
        exists: false,
        error: getApiError(error, 'Error al verificar el equipo'),
        loading: false
      });
    }
  };

  const handleCopyInfo = (asset) => {
    const info = `
Equipo: ${asset.nombre_equipo || 'N/A'}
Tipo: ${asset.tipo || 'N/A'}
Marca: ${asset.marca || 'N/A'}
Modelo: ${asset.modelo || 'N/A'}
Serial: ${asset.serial || 'N/A'}
RAM: ${asset.ram || 'N/A'}
Disco: ${asset.disco || 'N/A'}
Activo Fijo: ${asset.activo_fijo || 'N/A'}
Estado: ${asset.estado || 'N/A'}
${asset.empleado ? `Asignado a: ${asset.empleado}` : 'Sin asignar'}
    `.trim();

    try {
      // Crear un elemento textarea temporal
      const el = document.createElement('textarea');
      el.value = info;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);

      // Seleccionar y copiar el texto
      el.select();
      document.execCommand('copy');

      // Eliminar el elemento temporal
      document.body.removeChild(el);

      // Mostrar mensaje de éxito
      alert('Información copiada al portapapeles');
    } catch (err) {
      console.error('Error al copiar:', err);
      alert('Error al copiar la información');
    }
  };

  const handleVigilanciaSheet = async () => {
    try {
      // Obtener solo las laptops
      const laptops = data.filter(asset =>
        asset.tipo?.toUpperCase() === 'LAPTOP' &&
        asset.estado?.toLowerCase() === 'asignado'
      );

      if (laptops.length === 0) {
        alert('No hay laptops asignadas para generar la hoja de vigilancia');
        return;
      }

      const response = await axiosInstance.get('/api/activos/hoja-vigilancia', {
        responseType: 'blob'
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hoja_vigilancia_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error generando hoja de vigilancia:', error);
      alert(await getBlobApiError(error, 'Error al generar la hoja de vigilancia'));
    }
  };

  const handleUpdateAsset = async (assetId, updates) => {
    try {
      const response = await axiosInstance.patch(`/api/activos/${assetId}`, updates);

      if (response.data.message) {
        // Actualizar el estado local
        setData(prevData =>
          prevData.map(asset =>
            asset.id === assetId
              ? { ...asset, ...updates }
              : asset
          )
        );
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating asset:', error);
      return false;
    }
  };

  const EditableCell = ({ value, column, row, table, options, onSave, isBadgeType = false }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);

    useEffect(() => {
      console.log('Value changed:', { value, currentValue, column: column.id });
      setCurrentValue(value);
    }, [value]);

    const handleClick = (e) => {
      if (isBadgeType && value) {
        // Copiar al portapapeles con fallback
        const copyToClipboard = (text) => {
          // Método moderno
          if (navigator.clipboard && navigator.clipboard.writeText) {
            return navigator.clipboard.writeText(text);
          }

          // Fallback para navegadores antiguos o contextos no seguros
          return new Promise((resolve, reject) => {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            try {
              document.execCommand('copy');
              document.body.removeChild(textarea);
              resolve();
            } catch (err) {
              document.body.removeChild(textarea);
              reject(err);
            }
          });
        };

        copyToClipboard(value).then(() => {
          // Mostrar feedback visual temporal
          const badge = e.currentTarget.closest('.badge-cell');
          if (badge) {
            badge.classList.add('copied');
            setTimeout(() => {
              badge.classList.remove('copied');
            }, 300);
          }
        }).catch(err => {
          console.error('Error al copiar:', err);
        });
      }
    };

    const handleDoubleClick = () => {
      console.log('Double click, starting edit mode');
      setIsEditing(true);
    };

    const handleInputChange = (e) => {
      console.log('Input value changed:', e.target.value);
      setCurrentValue(e.target.value);
    };

    const handleChange = async (e) => {
      const newValue = e.target.value;
      console.log('handleChange called:', { newValue, field: column.id });

      try {
        let valueToSave = newValue;

        if (selectFields.includes(column.id)) {
          valueToSave = newValue;
          console.log('Using select value directly:', valueToSave);
        }

        console.log('Attempting to save:', { id: row.original.id, field: column.id, value: valueToSave });
        const success = await onSave(row.original.id, column.id, valueToSave);
        console.log('Save result:', success);

        if (success) {
          setCurrentValue(newValue);
        } else {
          console.log('Save failed, reverting to:', value);
          setCurrentValue(value);
        }
      } catch (error) {
        console.error('Error in handleChange:', error);
        setCurrentValue(value);
      }
      setIsEditing(false);
    };

    const handleBlur = () => {
      console.log('Input/Select blurred');
      setIsEditing(false);
    };

    const textFields = ['modelo', 'nombre_equipo', 'serial', 'activo_fijo', 'procesador'];
    const selectFields = ['sede', 'tipo', 'marca', 'ram', 'disco'];
    const isTextField = textFields.includes(column.id);
    const isSelectField = selectFields.includes(column.id);

    if (isEditing && (isTextField || isSelectField)) {
      if (isTextField) {
        return (
          <input
            type="text"
            value={currentValue || ''}
            onChange={handleInputChange}
            onBlur={handleChange}
            autoFocus
            className="editable-cell-input"
          />
        );
      }

      if (isSelectField) {
        if (column.id === 'tipo') {
          return (
            <select
              value={currentValue || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              autoFocus
            >
              <option value="">Seleccionar...</option>
              {['LAPTOP', 'DESKTOP', 'AIO'].map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );
        }

        return (
          <select
            value={currentValue || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            autoFocus
          >
            <option value="">Seleccionar...</option>
            {Array.isArray(options) ?
              options.map((option) => (
                <option key={option.id || option} value={option.nombre || option}>
                  {option.nombre || option}
                </option>
              ))
              : null
            }
          </select>
        );
      }
    }

    return (
      <div
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        className={`editable-cell ${isBadgeType ? 'badge-type' : ''}`}
        title={isBadgeType ? `Click para copiar, doble click para editar` : 'Doble click para editar'}
      >
        {value || 'Sin asignar'}
      </div>
    );
  };

  const availableFilters = [
    {
      id: 'sede',
      placeholder: 'Todas las sedes'
    },
    {
      id: 'tipo',
      placeholder: 'Todos los tipos'
    },
    {
      id: 'marca',
      placeholder: 'Todas las marcas'
    },
    {
      id: 'estado',
      placeholder: 'Todos los estados'
    }
  ];

  const handleExportToCSV = () => {
    try {
      // Preparamos los datos para CSV, solo usando las columnas visibles
      const visibleColumns = columns
        .filter(col => columnVisibility[col.accessorKey || col.id])
        .filter(col => col.id !== 'acciones') // Excluimos la columna de acciones
        .map(col => ({
          key: col.accessorKey || col.id,
          header: typeof col.header === 'string' ? col.header : col.accessorKey || col.id
        }));

      // Se exportan las filas que quedan tras la búsqueda y los filtros de la tabla
      const rows = filteredRows;

      // Agregar columna de comentarios si hay notas
      const exportColumns = [...visibleColumns];
      const hasNotas = rows.some(row => row.notas);
      if (hasNotas) {
        exportColumns.push({ key: 'notas', header: 'Comentarios' });
      }

      // Creamos el encabezado del CSV
      let csvContent = exportColumns.map(col => `"${col.header}"`).join(',') + '\n';

      // Añadimos los datos de cada fila
      rows.forEach(row => {
        const rowData = exportColumns.map(col => {
          // Aseguramos que los valores no rompan el formato CSV
          let value = row[col.key] !== null && row[col.key] !== undefined ? row[col.key] : '';

          // Si es la columna de estado y está asignado, mostrar el nombre del empleado
          if (col.key === 'estado' && row.estado?.toLowerCase() === 'asignado') {
            if (row.empleado) {
              value = row.empleado;
            } else if (row.asignacion_manual) {
              value = row.asignacion_manual;
            }
          }

          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvContent += rowData.join(',') + '\n';
      });

      // Creamos un objeto Blob con el contenido CSV. El BOM (\uFEFF) le indica
      // a Excel que el archivo es UTF-8; sin él, las tildes salen rotas
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });

      // Creamos un enlace para descargar el archivo
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'Equipos.csv');
      link.style.visibility = 'hidden';

      // Añadimos el enlace al DOM, hacemos clic y lo eliminamos
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error al exportar a CSV:", error);
      alert("Error al exportar a CSV. Por favor, inténtelo de nuevo.");
    }
  };

  return (
    <div className="assets-view">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
        <h2 className="page-title">Equipos ({filteredCount})</h2>
        <div className="flex gap-3 items-center">
          <button
            className="btn-secondary"
            onClick={() => setShowAISummary(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#7c3aed', borderColor: '#c4b5fd' }}
          >
            <Sparkles size={14} />
            Resumen IA
          </button>
          <button className="btn-secondary" onClick={() => setShowTimeline(true)}>
            <FaHistory />
            Historial
          </button>
          <button className="btn-primary" onClick={() => setShowNewAssetModal(true)}>
            <FaPlus />
            Agregar Equipo
          </button>
        </div>
      </div>

      <TableView
        data={data}
        columns={columns}
        loading={loading}
        error={error}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        defaultPageSize={30}
        defaultSorting={[{ id: 'activo_fijo', desc: true }]}
        activeActionMenu={activeActionMenu}
        setActiveActionMenu={setActiveActionMenu}
        availableFilters={availableFilters}
        onFilteredCountChange={setFilteredCount}
        onFilteredRowsChange={setFilteredRows}
      />

      <div className="export-button-container">
        <button
          className="export-button"
          onClick={handleExportToCSV}
          disabled={loading || filteredRows.length === 0}
        >
          <FaDownload /> Exportar a Excel
        </button>
      </div>

      {showModal && selectedAsset && (
        <EmployeesWithoutEquipmentModal
          onClose={() => setShowModal(false)}
          onAssign={handleAssignSuccess}
          asset={selectedAsset}
        />
      )}

      {showUnassignModal && selectedAsset && (
        <UnassignAssetModal
          asset={selectedAsset}
          onClose={() => setShowUnassignModal(false)}
          onUnassign={handleUnassignSuccess}
        />
      )}

      {showDeleteModal && selectedAsset && (
        <DeleteAssetModal
          asset={selectedAsset}
          onClose={() => setShowDeleteModal(false)}
          onSuccess={handleDeleteSuccess}
        />
      )}

      {showNewAssetModal && (
        <NewAssetModal
          onClose={() => setShowNewAssetModal(false)}
          onAssetAdded={handleAssetAdded}
        />
      )}

      {showAssetModal && selectedAsset && (
        <AssetModal
          asset={selectedAsset}
          onClose={() => {
            setShowAssetModal(false);
            setSelectedAsset(null);
          }}
          onUpdate={fetchData}
        />
      )}

      {showEmployeeModal && selectedEmployee && (
        <EmployeeModal
          employee={selectedEmployee}
          onClose={() => {
            setShowEmployeeModal(false);
            setSelectedEmployee(null);
          }}
        />
      )}

      {showTimeline && (
        <TimelineView
          categoria="assets"
          onClose={() => setShowTimeline(false)}
        />
      )}

      {showIndividualTimeline && selectedAssetForTimeline && (
        <TimelineView
          categoria="assets"
          itemId={selectedAssetForTimeline.id}
          itemName={`${selectedAssetForTimeline.tipo} ${selectedAssetForTimeline.marca} ${selectedAssetForTimeline.modelo}`}
          onClose={() => {
            setShowIndividualTimeline(false);
            setSelectedAssetForTimeline(null);
          }}
        />
      )}

      <Modal
        isOpen={showConstanciaModal && !!selectedAsset}
        onClose={() => {
          setShowConstanciaModal(false);
          setSelectedAsset(null);
        }}
        size="sm"
      >
        <Modal.Header onClose={() => {
          setShowConstanciaModal(false);
          setSelectedAsset(null);
        }}>
          Confirmar Impresión
        </Modal.Header>
        <Modal.Body>
          <p>¿Desea generar la constancia de entrega?</p>
          <div className="asset-info">
            <p><strong>Equipo:</strong> {selectedAsset?.nombre_equipo}</p>
            <p><strong>Serial:</strong> {selectedAsset?.serial}</p>
            <p><strong>Empleado:</strong> {selectedAsset?.empleado}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            onClick={() => {
              setShowConstanciaModal(false);
              setSelectedAsset(null);
            }}
            className="btn-secondary"
          >
            No Generar
          </button>
          <button
            onClick={async () => {
              if (generatingPdf) return;
              setGeneratingPdf(true);
              try {
                const response = await axiosInstance.get(`/api/activos/${selectedAsset.id}/constancia`, {
                  responseType: 'blob'
                });

                const blob = new Blob([response.data]);
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `constancia_entrega_${selectedAsset.empleado}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              } catch (error) {
                console.error('Error generando constancia:', error);
                toast.error(await getBlobApiError(error, 'Error al generar la constancia'));
              } finally {
                setGeneratingPdf(false);
              }
              setShowConstanciaModal(false);
              setSelectedAsset(null);
            }}
            className="btn-primary"
            disabled={generatingPdf}
          >
            {generatingPdf ? 'Generando...' : 'Generar PDF'}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        isOpen={showADVerifyModal && !!adEquipmentInfo}
        onClose={() => {
          setShowADVerifyModal(false);
          setADEquipmentInfo(null);
        }}
        size="md"
      >
        <Modal.Header onClose={() => {
          setShowADVerifyModal(false);
          setADEquipmentInfo(null);
        }}>
          Verificación en Active Directory
        </Modal.Header>
        <Modal.Body>
          {adEquipmentInfo?.loading ? (
            <p>Verificando equipo en AD...</p>
          ) : adEquipmentInfo?.error ? (
            <div className="error-message">
              <p>{adEquipmentInfo.error}</p>
            </div>
          ) : (
            <div className="ad-info">
              <p><strong>Nombre del equipo:</strong> {adEquipmentInfo?.expectedName}</p>
              <p><strong>Estado en AD:</strong> {
                adEquipmentInfo?.exists ?
                  <span className="status-success">Encontrado en AD</span> :
                  <span className="status-error">No encontrado en AD</span>
              }</p>
              {adEquipmentInfo?.exists && (
                <>
                  <p><strong>Estado de conexión:</strong> {
                    adEquipmentInfo.estado === 'verde' ?
                      <span className="status-success">Conectado</span> :
                      <span className="status-error">Desconectado</span>
                  }</p>
                  {adEquipmentInfo.operating_system && (
                    <p><strong>Sistema Operativo:</strong> {adEquipmentInfo.operating_system}</p>
                  )}
                  {adEquipmentInfo.last_logon && (
                    <p><strong>Último inicio de sesión:</strong> {adEquipmentInfo.last_logon}</p>
                  )}
                  {adEquipmentInfo.dias_inactivo !== null && (
                    <p><strong>Días inactivo:</strong> {
                      adEquipmentInfo.dias_inactivo > 30 ?
                        <span className="status-error">{adEquipmentInfo.dias_inactivo} días</span> :
                        <span className="status-success">{adEquipmentInfo.dias_inactivo} días</span>
                    }</p>
                  )}
                  {adEquipmentInfo.last_changed && (
                    <p><strong>Último cambio:</strong> {adEquipmentInfo.last_changed}</p>
                  )}
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            onClick={() => {
              setShowADVerifyModal(false);
              setADEquipmentInfo(null);
            }}
            className="btn-primary"
          >
            Cerrar
          </button>
        </Modal.Footer>
      </Modal>

      {showAssignmentTimeline && selectedAssetForAssignments && (
        <AssignmentTimeline
          tipo="asset"
          equipoId={selectedAssetForAssignments.id}
          equipoNombre={`${selectedAssetForAssignments.tipo} ${selectedAssetForAssignments.marca} ${selectedAssetForAssignments.modelo}`}
          onClose={() => {
            setShowAssignmentTimeline(false);
            setSelectedAssetForAssignments(null);
          }}
        />
      )}

      {showAISummary && (
        <InventoryAISummary
          tipo="assets"
          onClose={() => setShowAISummary(false)}
        />
      )}
    </div>
  );
}

export default AssetsView;